<template>
    <page-template page-id="login" ref="super">
      <document-title :title="$tb('pages.login.documentTitle')"></document-title>

        <template v-slot:before v-if="isTitlebarActive">
            <titlebar :text="$tb('pages.login.header', {0: $store.state.appName})"></titlebar>
        </template>

        <div class="ui column" v-if="areCookiesActive()">

            <brand-header id="login-header"
                          :text="areCookiesActive() ? $t('pages.login.title') : $t('errors.cookies.header')"
                          :has-extension="hasHeaderExtension"
                          :has-logo="hasBrandLogo">
            </brand-header>

            <p v-if="!mobileLoginDisabled" id="login-subtitle" v-html="$tb('pages.login.subheader', {
                0: $store.state.appName,
                1: $store.state.username!==null ? 'confirm' : 'enter'
            })"></p>

            <p v-else id="login-subtitle" v-html="$tb('pages.login.subheaderEmail', {
                0: $store.state.appName,
                1: $store.state.username!==null ? 'confirm' : 'enter'
            })"></p>

            <messages id="Notifications" ref="postman" error-element-id="server-errors" aria-role="none"></messages>

            <validated-field :show-errors-on="showErrors" :has-any-errors="$v.username.$invalid"
                             id="username-errors"
                             :validation-rules="[
                             {value: $v.username.required, msg: usernameEmptyError},
                             {value: $v.username.validEmail, showOn: $v.username.required && (!useMobile || $v.username.hasOwnProperty('email')), msg: $t('common.validation.username.emailFormat')},
                             {value: $v.username.validMobile, showOn: $v.username.required && (useMobile && !$v.username.hasOwnProperty('email')), msg: $t('common.validation.username.mobileFormat')}]">

                <labeled-input-field v-if="!useMobile" id="username" v-model="$v.username.$model"
                                     autocomplete="username"
                                     type="email"
                                     :label="$t('common.label.email')"
                                     :mandatory="true"
                                     v-on:input="userChecked=false"
                                     aria-error-element-id="username-errors server-errors"
                                     v-on:submit="checkUser"
                                     @enter-pressed="checkUser"
                                     :invalid="$v.username.$invalid && showErrors">
                </labeled-input-field>

                <labeled-input-field v-else id="username" v-model="$v.username.$model"
                                     autocomplete="username"
                                     type="tel"
                                     :label="$t('common.label.mobile')"
                                     :mandatory="true"
                                     v-on:input="userChecked=false"
                                     aria-error-element-id="username-errors server-errors"
                                     @enter-pressed="checkUser"
                                     v-on:submit="checkUser"
                                     @input="onMobileInput"
                                     :invalid="$v.username.$invalid && showErrors">
                </labeled-input-field>
            </validated-field>

            <div v-if="!mobileLoginDisabled" class="field left aligned">
                <div id="usernameMethod" class="ui toggle checkbox">
                    <input tabindex="0" id="mobileSwitch" type="checkbox" :checked="useMobile" @click="switchType"/>
                    <label for="mobileSwitch"
                           v-text="useMobile ? $t('pages.registration.useMobileChecked') : $t('pages.registration.useMobile')"></label>
                </div>
            </div>

            <div class="field" v-if="!userChecked">
                <button id="continue" type="button" class="ui fluid primary button" @click.prevent="checkUser">
                    {{ $t('common.button.continue') }}
                </button>
            </div>

            <div class="field" v-if="showPass">
                <router-link id="login-with-password" class="ui fluid primary button" tag="button"
                             :to="{name: 'GOTO_LOGIN_PASS'}">
                    {{ $t('pages.login.button.loginWithPass') }}
                </router-link>
            </div>

            <div class="field" v-if="showOTP">
                <button id="login-with-otp" type="submit" v-on:click="requestTAN"
                        :class="showPass ? 'ui fluid basic button' : 'ui fluid primary button'">
                    {{ $t('pages.login.button.loginWithOTP' )}}
                </button>
            </div>

            <slotted-field v-if="showRegistrationLink && hideRegistration"
                           :right-slot-method="gotoRegistration"
                           :right-slot-info-text="$tb('pages.login.areyounew')"
                           :right-slot-link-text="$t('pages.login.register')"></slotted-field>

        </div>
        <div class="ui column" v-if="!areCookiesActive()">
            <h4 id="login-nocookies-header" class="custom-ui header">{{ $t('errors.cookies.header') }}</h4>
            <p id="login-nocookies-subtitle">{{ $t('errors.cookies.subheader') }}</p>
        </div>
      </page-template>
</template>
<script>
import PageTemplate from './template/PageTemplate.vue'
import Titlebar from '../components/Titlebar.vue'
import BrandHeader from '../components/BrandHeader.vue'
import LabeledInputField from '../components/LabeledInputField.vue'
import Messages from '../components/Messages.vue'
import SlottedField from '../components/SlottedField.vue'
import ValidatedField from '../components/ValidatedField.vue'
import { ROUTE_REGISTRATION } from '../router'
import { mixin } from '../js/ciam-mixin.js'
import { brand } from '../js/brand.js'
import { getRecaptchaToken } from '../js/helpers.js'
import * as validate from '../js/validation.js'
import { required } from 'vuelidate/lib/validators'
import { AsYouType, getCountryCallingCode } from 'libphonenumber-js/mobile'
import Vue from 'vue'

export default {
  components: {
    PageTemplate,
    Titlebar,
    BrandHeader,
    LabeledInputField,
    Messages,
    SlottedField,
    ValidatedField
  },
  mixins: [mixin, brand],
  props: {
    usernameFromRegistration: {
      type: String
    }
  },
  data: function () {
    let initialUseMobile = this.$defaults.MOBILE_SWITCH_SELECTED
    let initialMobileValid

    if (this.isNotEmpty(this.$store.state.username)) {
      initialUseMobile = !this.isEmail(this.$store.state.username)

      const formattedNumber = new AsYouType()
      formattedNumber.input(this.$store.state.username)
      initialMobileValid = formattedNumber.isValid()
    }

    if (this.mobileLoginDisabled) {
      initialUseMobile = false
    }

    return {
      username: (this.usernameFromRegistration || this.$store.state.username)?.replace(/＠/g, '@'),
      hasPass: false,
      rememberMe: false,
      userChecked: false,
      isValidMobile: initialMobileValid != null ? initialMobileValid : false,
      showErrors: false,
      useMobile: initialUseMobile,
      isRecaptchaActive: this.$store.state.isRecaptchaActive,
      recaptchaSiteKey: this.$store.state.recaptchaSiteKey
    }
  },
  computed: {
    showOTP: function () {
      return this.supportAcr('OTP') && this.userChecked
    },
    showPass: function () {
      return this.supportAcr('PASSWORD') && this.userChecked && this.hasPass
    },
    hideRegistration: function () {
      return !(this.$store.state.hideRegistration)
    },
    usernameEmptyError: function () {
      return this.useMobile
        ? this.$t('common.validation.username.mobileEmpty')
        : this.$t('common.validation.username.emailEmpty')
    },
    mobileLoginDisabled: function () {
      return this.$store.state.isMobileLoginDisabled
    },
    isThirdParty: function () {
      return this.$store.state.isThirdParty
    }
  },
  validations () {
    return {
      username: this.useMobile && !this.mobileLoginDisabled ? {
        required,
        validMobile () {
          return this.isValidMobile
        }
      } : {
        required,
        validEmail (input) {
          return !!input && validate.validateEmail(input.trim())
        }
      }
    }
  },
  methods: {
    switchType: function () {
      this.useMobile = !this.useMobile

      const countryCode = getCountryCallingCode(this.getInitialCountry())

      this.username = this.useMobile ? '+' + countryCode : ''
      Vue.nextTick(this.focusUsername)
    },
    isValid: function () {
      this.$refs.postman.clear()
      this.showErrors = false
      this.username = this.username?.replace(/＠/g, '@')

      if (this.$v.$anyError || this.$v.$invalid) {
        const _this = this

        this.focusFirstErrorField(function () {
          _this.showErrors = true
        })

        return false
      }

      return true
    },
    checkUser: function (e) {
      if (!this.isValid()) {
        this.userChecked = false
        this.hasPass = false
        return false
      }

      const sanitizedUsername = this.sanitizeUsername(this.username, this.useMobile)

      console.debug(`Checking user [username=${sanitizedUsername}]`)

      this.$refs.super.startLoading()
      this.username = sanitizedUsername

      this.$api.login.checkUser(sanitizedUsername, this.checkUserOk, this.checkUserFail)
    },
    checkUserOk (resp) {
      try {
        sessionStorage.setItem('storedrid', this.$store.state.rid)
        sessionStorage.setItem('storedusername', this.username)
      } catch (e) {
        console.log('Error')
      }
      this.$store.commit('username', this.username)

      if (this.handleRoutingResponse(resp)) return

      this.userChecked = true
      this.hasPass = resp.hasPass

      console.debug(`User checked successfully [hasPass=${resp.hasPass}, language=${resp.lang}]`)

      Vue.nextTick(function () {
        const loginWithPassElem = $('#login-with-password')
        const loginWithOtpElem = $('#login-with-otp')

        if (resp.hasPass) {
          loginWithPassElem.focus()
        } else {
          loginWithOtpElem.focus()
        }
      })

      this.$refs.super.stopLoading()
    },
    checkUserFail: function (resp) {
      if (resp.status === 404) {
        this.userChecked = false
        this.hasPass = false
      }
      this.$refs.super.stopLoading()
      this.focusUsername()
      return this.onAjaxError(resp)
    },
    requestTAN: async function (e) {
      if (e) e.preventDefault()

      if (!this.isValid()) return

      this.$refs.super.startLoading()

      const recaptchaToken = await getRecaptchaToken(this.isRecaptchaActive, this.recaptchaSiteKey, this.$recaptcha.actions.LOGIN_OTP)

      this.$api.codes.sendOTP(this.username, recaptchaToken, this.handleRoutingResponse, this.onFail)
    },
    onFail: function (response) {
      this.onAjaxError(response)
      this.$refs.super.stopLoading()
    },
    gotoRegistration: function () {
      this.$router.push(
        {
          name: ROUTE_REGISTRATION,
          params: {
            formPrefill: {
              username: this.username
            }
          }
        }
      )
    },
    onMobileInput: function () {
      if (validate.validateEmail(this.username.trim())) return

      if (!this.username) {
        this.isValidMobile = false
        return
      }

      const asYouType = new AsYouType()
      this.username = asYouType.input(this.username.replace(/[^0-9+ ]/g, ''))
      this.isValidMobile = asYouType.isValid()
      $('#username').val(this.username)
    },
    checkHideErrors: function () {
      if (this.$v.$invalid) return
      this.hideErrors()
    },
    hideErrors: function () {
      const postman = $('form .ui.error.message')
      if (postman) {
        postman.empty()
      }
      this.showErrors = false
    },
    focusUsername: function () {
      const el = $('#username')
      if (el) {
        el.focus()
      }
    }
  },
  mounted: function () {
    console.log(`Login [showOTP=${this.showOTP}, showPass=${this.showPass}]`)

    this.focusUsername()
  },
  beforeDestroy: function () {
    jQuery('form.ui.form').form('remove field(username)')
    this.userChecked = false
    this.hasPass = false
    this.showErrors = false
  },
  watch: {
    username: {
      handler (newVal, oldVal) {
        if (!newVal) return
        console.debug('Triggered username change')
        const formattedNumber = new AsYouType()
        formattedNumber.input(newVal)

        if (formattedNumber.isValid() && !this.useMobile && !this.mobileLoginDisabled) {
          this.useMobile = true
          this.$v.username.$model = newVal
          this.isValidMobile = true
        }

        if (validate.validateEmail(newVal.trim()) && this.useMobile) {
          this.useMobile = false
          this.$v.username.$model = newVal
          this.isValidMobile = false
        }
      },
      immediate: true
    },
    $v: {
      handler () {
        this.checkHideErrors()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
  #login-subtitle {
    margin-top: 16px;
  }
</style>
